import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase';
import { SEASON_STATUS, fetchSeasons } from '../../../services/seasons';
import { useNotification } from '../../../context/NotificationContext';

const CHAMPIONS_LEAGUE_ID = 2;

/**
 * Maakt het seizoensdocument aan. Het importeren van clubs, selecties en kalender
 * gebeurt daarna op de pagina Stamgegevens van dat seizoen.
 *
 * Het document-id is de Sportmonks season-id — daar keyen auctions.season,
 * schedule/{seasonId}, playerScores/{seasonId} en players.seasonId op. Je kiest hem
 * daarom uit de lijst van Sportmonks in plaats van hem over te typen.
 */
function NewSeasonDialog({ open, onClose, onCreated }) {
    const notify = useNotification();
    const [available, setAvailable] = useState([]);
    const [existingIds, setExistingIds] = useState(new Set());
    const [selectedId, setSelectedId] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [displayNameTouched, setDisplayNameTouched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const selected = available.find((s) => s.id === selectedId) || null;

    useEffect(() => {
        if (!open) return;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const getAvailableSeasons = httpsCallable(functions, 'getAvailableSeasons');
                const [result, known] = await Promise.all([
                    getAvailableSeasons(),
                    fetchSeasons(),
                ]);
                if (cancelled) return;
                setAvailable(result.data || []);
                setExistingIds(new Set(known.map((s) => String(s.id))));
            } catch (err) {
                console.error('Kon seizoenen bij Sportmonks niet ophalen:', err);
                if (!cancelled) {
                    notify.error('De seizoenen konden niet bij Sportmonks opgehaald worden: ' + err.message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [open, notify]);

    const reset = () => {
        setSelectedId('');
        setDisplayName('');
        setDisplayNameTouched(false);
        setError('');
    };

    const handleClose = () => {
        if (saving) return;
        reset();
        onClose();
    };

    const handleSelect = (id) => {
        setSelectedId(id);
        // Vul de weergavenaam automatisch, tenzij de gebruiker hem zelf al aanpaste.
        if (!displayNameTouched) {
            const season = available.find((s) => s.id === id);
            setDisplayName(season ? `Champions League ${season.name}` : '');
        }
    };

    const handleCreate = async () => {
        if (!selected) {
            notify.error('Kies eerst een seizoen.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            await setDoc(doc(db, 'seasons', selected.id), {
                name: selected.name,
                displayName: displayName.trim() || `Champions League ${selected.name}`,
                status: SEASON_STATUS.VOORBEREIDING,
                competitionId: CHAMPIONS_LEAGUE_ID,
                startDate: selected.startingAt || null,
                endDate: selected.endingAt || null,
                createdAt: serverTimestamp(),
            });

            const newId = selected.id;
            reset();
            onCreated(newId);
        } catch (err) {
            console.error('Kon seizoen niet aanmaken:', err);
            notify.error('Het seizoen kon niet aangemaakt worden. Ben je ingelogd als administrator?');
        } finally {
            setSaving(false);
        }
    };

    const alreadyExists = selectedId && existingIds.has(selectedId);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Nieuw seizoen starten</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Kies het competitieseizoen bij Sportmonks. Het seizoen wordt aangemaakt in
                    voorbereiding; clubs, selecties en de kalender importeer je daarna bij Stamgegevens.
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <FormControl fullWidth disabled={available.length === 0}>
                            <InputLabel id="season-select-label">Competitie &amp; seizoen</InputLabel>
                            <Select
                                labelId="season-select-label"
                                value={selectedId}
                                label="Competitie & seizoen"
                                onChange={(e) => handleSelect(e.target.value)}
                            >
                                {available.map((season) => {
                                    const exists = existingIds.has(season.id);
                                    return (
                                        <MenuItem key={season.id} value={season.id} disabled={exists}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                <span>UEFA Champions League {season.name}</span>
                                                {season.isCurrent && (
                                                    <Chip size="small" label="Huidig" color="success" variant="outlined" />
                                                )}
                                                {exists && (
                                                    <Chip size="small" label="Bestaat al" variant="outlined" />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        {available.length === 0 && !error && (
                            <Alert severity="info">
                                Sportmonks gaf geen seizoenen terug voor deze competitie.
                            </Alert>
                        )}

                        {selected && (
                            <Typography variant="caption" color="text.secondary">
                                Sportmonks-id {selected.id}
                                {selected.startingAt ? ` · loopt van ${selected.startingAt} tot ${selected.endingAt || '?'}` : ''}
                            </Typography>
                        )}

                        <TextField
                            label="Weergavenaam"
                            value={displayName}
                            onChange={(e) => { setDisplayName(e.target.value); setDisplayNameTouched(true); }}
                            placeholder="Champions League 2026/2027"
                            helperText="Zo zien deelnemers het seizoen."
                            fullWidth
                            disabled={!selected}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>Annuleren</Button>
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={saving || loading || !selected || alreadyExists}
                >
                    {saving ? 'Bezig…' : 'Seizoen aanmaken'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default NewSeasonDialog;
